// Single source of truth for customer-facing plan facts (price, endpoint cap,
// retention). Marketing copy stays with each page; the facts live here so the
// landing pricing table, the signup wizard, and the MSP add-tenant dialog can
// never drift apart (see go-live docs/website sync criterion).

export type PlanId = "starter" | "essential" | "growth" | "business" | "enterprise";

export interface PlanCatalogEntry {
  id: PlanId;
  name: string;
  /** Price figure without the period suffix: "Free" | "$200" | "Custom". */
  price: string;
  /** Period suffix rendered after the price: "/mo" or "". */
  per: "" | "/mo";
  /** Endpoint cap as a display number, or null for unlimited. */
  endpointsCap: string | null;
  /** Retention line, shared verbatim by all surfaces. */
  retention: string;
  popular?: boolean;
  /** Hidden plans stay valid for existing tenants but are not shown or selectable on public surfaces. */
  hidden?: boolean;
}

export const PLAN_CATALOG: readonly PlanCatalogEntry[] = [
  { id: "starter", name: "Starter", price: "Free", per: "", endpointsCap: "10", retention: "7-day retention" },
  { id: "essential", name: "Essential", price: "$200", per: "/mo", endpointsCap: "20", retention: "30-day retention" },
  { id: "growth", name: "Growth", price: "$499", per: "/mo", endpointsCap: "60", retention: "60-day retention", popular: true },
  { id: "business", name: "Business", price: "$999", per: "/mo", endpointsCap: "250", retention: "90-day retention" },
  // Enterprise withheld from the public site for now — drop `hidden` to bring it back.
  { id: "enterprise", name: "Enterprise", price: "Custom", per: "", endpointsCap: null, retention: "1-year retention", hidden: true },
];

export const PLAN_BY_ID = Object.fromEntries(PLAN_CATALOG.map((p) => [p.id, p])) as Record<PlanId, PlanCatalogEntry>;

const PLAN_ID_SET: ReadonlySet<string> = new Set(PLAN_CATALOG.map((p) => p.id));

export function isPlanId(value: string | null): value is PlanId {
  return value !== null && PLAN_ID_SET.has(value);
}

/** "Up to 20 endpoints" / "Unlimited endpoints" (landing-style bullet). */
export function endpointsBullet(p: PlanCatalogEntry): string {
  return p.endpointsCap ? `Up to ${p.endpointsCap} endpoints` : "Unlimited endpoints";
}
